import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DICT, SUPPORTED, DEFAULT_LANG, detectInitialLang, stripLangPrefix, withLang } from "@/lib/i18n";

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const urlLang = SUPPORTED.includes(params.lang) ? params.lang : null;
  const initial = useMemo(() => urlLang || detectInitialLang() || DEFAULT_LANG, []); // eslint-disable-line
  const [lang, setLangState] = useState(initial);

  // Sync URL → state
  useEffect(() => {
    const segLang = location.pathname.split("/").filter(Boolean)[0];
    const fromUrl = SUPPORTED.includes(segLang) ? segLang : "de";
    if (fromUrl !== lang) {
      setLangState(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-redirect on initial visit if no URL prefix and detected lang is non-DE
  // Excludes language-agnostic routes (admin) so /admin/login isn't rewritten to /en/admin/login.
  useEffect(() => {
    const segLang = location.pathname.split("/").filter(Boolean)[0];
    const isLangAgnostic = location.pathname.startsWith("/admin");
    if (!SUPPORTED.includes(segLang) && initial !== "de" && !isLangAgnostic) {
      navigate(withLang(location.pathname, initial), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = useCallback(
    (next) => {
      if (!SUPPORTED.includes(next)) return;
      localStorage.setItem("kb_lang", next);
      setLangState(next);
      // Language-agnostic routes (admin) must not get a lang prefix
      if (location.pathname.startsWith("/admin")) return;
      const newPath = withLang(location.pathname, next);
      navigate(newPath + location.search + location.hash);
    },
    [location, navigate]
  );

  const t = useCallback(
    (key, vars = null) => {
      const parts = key.split(".");
      let cur = DICT[lang] || DICT.de;
      for (const p of parts) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else {
          // fallback to DE
          cur = DICT.de;
          for (const pp of parts) {
            if (cur && typeof cur === "object" && pp in cur) cur = cur[pp];
            else return key;
          }
          break;
        }
      }
      if (vars && typeof cur === "string") {
        for (const [k, v] of Object.entries(vars)) {
          cur = cur.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
        }
      }
      return cur;
    },
    [lang]
  );

  // Helper for backend multilingual fields ({ de, en, fr } -> string)
  const tl = useCallback(
    (field) => {
      if (field == null) return "";
      if (typeof field === "string") return field;
      if (typeof field === "object")
        return field[lang] || field.de || field.en || field.fr || "";
      return String(field);
    },
    [lang]
  );

  const pathWithLang = useCallback(
    (path) => withLang(path, lang),
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, tl, pathWithLang, supported: SUPPORTED, dict: DICT[lang] }),
    [lang, setLang, t, tl, pathWithLang]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}

export { stripLangPrefix };
